import { Account, AccountResourceInfo, MfaOnboardingResourceInfo, MfaOnboarding } from 'we-package-react-account';
import { useScopedSelector } from 'ia-react-core';
import { useLocation } from 'react-router-dom';
import { useResourceState } from 'omni-package-react-core';
import { OnboardingResourceInfo } from '~/components/OnboardingResource';
import OnboardingStatus from '~/interfaces/OnboardingStatus';
import { OnboardingData } from '~/interfaces/OnboardingData';
import Constants from '~/app/Constants';
import useIsFrom from '~/hooks/useIsFrom';
import { Company } from '~/interfaces/CompanyConfig';

export default function useAccountSecurityState() {
  const isFromIaah = useIsFrom(Company.IAAH);
  const account: Account = useScopedSelector(AccountResourceInfo.dataDst);
  const mfaOnboarding: MfaOnboarding = useScopedSelector(MfaOnboardingResourceInfo.dataDst);
  const mfaOnboardingStatus = (mfaOnboarding?.data as { onboardingStatus: string })?.onboardingStatus;
  const centralizationOnboarding: OnboardingData = useScopedSelector(OnboardingResourceInfo.dataDst);
  const centralizationOnboardingStatus: OnboardingStatus = centralizationOnboarding?.status;

  const accountState = useResourceState(AccountResourceInfo.name);
  const mfaOnboardingState = useResourceState(MfaOnboardingResourceInfo.name);
  const centralizationOnboardingState = useResourceState(OnboardingResourceInfo.name);

  const location = useLocation();
  const isPublicRouteWithAuthenticatedLayout = Constants.PublicRoutesWithAuthenticatedLayout.includes(location.pathname);

  const isAccountLoading = !isFromIaah && Boolean(!account && !accountState?.error && (accountState?.pending !== false || accountState.requestCount === 0));
  const isMfaOnboardingLoading = !isFromIaah && Boolean(!mfaOnboardingStatus && !mfaOnboardingState?.error && (mfaOnboardingState?.pending !== false || mfaOnboardingState?.requestCount === 0));
  const isCentralizationOnboardingLoading = !isPublicRouteWithAuthenticatedLayout
    && Boolean(!centralizationOnboardingStatus && !centralizationOnboardingState?.error && (centralizationOnboardingState?.pending !== false || centralizationOnboardingState.requestCount === 0));

  const showConfirmPage = (location.state as {
    data?: {
      showConfirmPage?: boolean;
    };
  })?.data?.showConfirmPage;

  const currentPath = location.pathname;
  const currentSearch = location.search;

  const isCentralizationFlagRequired = localStorage.getItem('we-centralization-onboarding') === 'true';
  const shouldUseCentralizationFlag = !centralizationOnboardingStatus && !centralizationOnboardingState?.error;

  const isUsernameValidationRequired = account?.behaviours?.isUsernameValidationRequired || showConfirmPage || localStorage.getItem('we-account-isUsernameValidationRequired') === 'true';
  const isSecurityInfoValidationRequired = account?.behaviours?.isSecurityInformationValidationRequired || showConfirmPage || localStorage.getItem('we-account-isSecurityInformationValidationRequired') === 'true';
  const isCentralizationRequired = centralizationOnboardingStatus !== OnboardingStatus.NoPrimaryEmail
    && (centralizationOnboardingStatus === OnboardingStatus.Active
      || showConfirmPage
      || (shouldUseCentralizationFlag && isCentralizationFlagRequired));

  const hasOnboardingError = Boolean(centralizationOnboardingState?.error);

  return {
    mfaOnboardingStatus,
    centralizationOnboardingStatus,
    isAccountLoading,
    isMfaOnboardingLoading,
    isCentralizationOnboardingLoading,
    isUsernameValidationRequired,
    isSecurityInfoValidationRequired,
    isCentralizationRequired,
    hasOnboardingError,
    currentPath,
    currentSearch,
  };
}
